/**
 * Pure-TypeScript 4-point perspective transform (planar homography).
 *
 * We solve the classic 8-equation linear system that maps a source quad
 * (e.g. four court landmarks in pixel space) to a destination quad
 * (e.g. the canonical ITF court rectangle in metres). No OpenCV.js — a
 * 6 MB WASM blob for one 8x8 solve is overkill.
 *
 * Convention: a homography `H` is a 3x3 matrix stored row-major in a
 * length-9 tuple. Applied to a point `(x, y, 1)^T`, the result `(X, Y, W)`
 * is re-normalised to `(X/W, Y/W)`.
 */

export type Matrix3x3 = readonly [
  number, number, number,
  number, number, number,
  number, number, number,
];

export type Point = readonly [number, number];
export type Quad = readonly [Point, Point, Point, Point];

/**
 * ITF court dimensions in metres. See ADR 0008.
 * These are the canonical targets for `pixelToCourt`.
 */
export const COURT_M = {
  /** Full length baseline-to-baseline. */
  LENGTH: 23.77,
  /** Singles sideline-to-sideline. */
  SINGLES_WIDTH: 8.23,
  /** Doubles sideline-to-sideline. */
  DOUBLES_WIDTH: 10.97,
  /** Service line distance from the net. */
  SERVICE_LINE: 6.40,
} as const;

/** Error thrown when the linear system is singular (collinear source points). */
export class SingularHomographyError extends Error {
  constructor(message = 'Homography is singular (collinear or degenerate quad)') {
    super(message);
    this.name = 'SingularHomographyError';
  }
}

/**
 * Compute the 3x3 homography that maps `src[i]` to `dst[i]` for i in 0..3.
 * Uses the standard DLT formulation: 4 point correspondences yield 8 linear
 * equations in 8 unknowns (with h33 normalised to 1). Solved via Gaussian
 * elimination with partial pivoting.
 *
 * Throws `SingularHomographyError` if the quad is degenerate (e.g. three
 * collinear points) so callers can fall back to manual calibration.
 */
export function computeHomography(src: Quad, dst: Quad): Matrix3x3 {
  // For each correspondence (x,y) -> (X,Y), we get two equations:
  //   x·h11 + y·h12 + h13 + 0 + 0 + 0 - x·X·h31 - y·X·h32 = X
  //   0 + 0 + 0 + x·h21 + y·h22 + h23 - x·Y·h31 - y·Y·h32 = Y
  // Unknown vector: h = [h11,h12,h13,h21,h22,h23,h31,h32]^T.
  const A: number[][] = [];
  const b: number[] = [];
  for (let i = 0; i < 4; i++) {
    const s = src[i]!;
    const d = dst[i]!;
    const [x, y] = s;
    const [X, Y] = d;
    A.push([x, y, 1, 0, 0, 0, -x * X, -y * X]);
    b.push(X);
    A.push([0, 0, 0, x, y, 1, -x * Y, -y * Y]);
    b.push(Y);
  }

  const h = solveLinearSystem(A, b);
  // Reassemble into a 3x3 with h33 = 1.
  return [
    h[0]!, h[1]!, h[2]!,
    h[3]!, h[4]!, h[5]!,
    h[6]!, h[7]!, 1,
  ];
}

/**
 * Apply a homography to a 2D point. Returns null if the projected `w`
 * component is zero (point lies on the line at infinity).
 */
export function applyHomography(p: Point, H: Matrix3x3): Point | null {
  const [x, y] = p;
  const X = H[0] * x + H[1] * y + H[2];
  const Y = H[3] * x + H[4] * y + H[5];
  const W = H[6] * x + H[7] * y + H[8];
  if (Math.abs(W) < 1e-12) return null;
  return [X / W, Y / W];
}

/**
 * Project a pixel coordinate onto the court plane (metres). The caller is
 * responsible for having computed `H` that maps pixels -> court metres.
 */
export function pixelToCourt(p: Point, H: Matrix3x3): { x: number; y: number } {
  const out = applyHomography(p, H);
  if (out === null) {
    // Callers should filter these events; a ball "at infinity" has no
    // physical meaning on the court plane.
    return { x: NaN, y: NaN };
  }
  return { x: out[0], y: out[1] };
}

/**
 * Inverse projection: court metres -> pixel coordinates, for overlay
 * rendering. Uses the analytic 3x3 inverse of `H`.
 */
export function courtToPixel(p: Point, H: Matrix3x3): Point {
  const Hinv = invert3x3(H);
  const out = applyHomography(p, Hinv);
  if (out === null) return [NaN, NaN];
  return out;
}

/** Invert a 3x3 matrix. Throws on singular input. */
export function invert3x3(m: Matrix3x3): Matrix3x3 {
  const [a, b, c, d, e, f, g, h, i] = m;
  // Cofactors (transpose of cofactor matrix = adjugate).
  const c00 = e * i - f * h;
  const c01 = -(d * i - f * g);
  const c02 = d * h - e * g;
  const c10 = -(b * i - c * h);
  const c11 = a * i - c * g;
  const c12 = -(a * h - b * g);
  const c20 = b * f - c * e;
  const c21 = -(a * f - c * d);
  const c22 = a * e - b * d;
  const det = a * c00 + b * c01 + c * c02;
  if (Math.abs(det) < 1e-12) {
    throw new SingularHomographyError('Cannot invert: determinant is zero');
  }
  const inv = 1 / det;
  // adjugate is transpose of cofactor matrix -> swap rows/cols.
  return [
    c00 * inv, c10 * inv, c20 * inv,
    c01 * inv, c11 * inv, c21 * inv,
    c02 * inv, c12 * inv, c22 * inv,
  ];
}

/**
 * Gaussian elimination with partial pivoting. Takes an 8x8 matrix `A` and
 * 8-vector `b`, returns `x` such that `A x = b`. Throws
 * `SingularHomographyError` if a pivot is numerically zero.
 */
function solveLinearSystem(A: number[][], b: number[]): number[] {
  const n = A.length;
  // Build augmented matrix (copy to avoid mutating caller data).
  const M: number[][] = A.map((row, i) => [...row, b[i]!]);

  for (let i = 0; i < n; i++) {
    // Partial pivot: find row with largest absolute value in column i.
    let pivot = i;
    let pivotValue = Math.abs(M[i]![i]!);
    for (let r = i + 1; r < n; r++) {
      const v = Math.abs(M[r]![i]!);
      if (v > pivotValue) {
        pivotValue = v;
        pivot = r;
      }
    }
    if (pivotValue < 1e-12) {
      throw new SingularHomographyError();
    }
    if (pivot !== i) {
      const tmp = M[i]!;
      M[i] = M[pivot]!;
      M[pivot] = tmp;
    }
    // Eliminate below.
    const pivotRow = M[i]!;
    const pivotDiag = pivotRow[i]!;
    for (let r = i + 1; r < n; r++) {
      const row = M[r]!;
      const factor = row[i]! / pivotDiag;
      if (factor === 0) continue;
      for (let c = i; c <= n; c++) {
        row[c] = row[c]! - factor * pivotRow[c]!;
      }
    }
  }

  // Back-substitution.
  const x: number[] = new Array(n).fill(0);
  for (let i = n - 1; i >= 0; i--) {
    const row = M[i]!;
    let sum = row[n]!;
    for (let c = i + 1; c < n; c++) {
      sum -= row[c]! * x[c]!;
    }
    x[i] = sum / row[i]!;
  }
  return x;
}
